/**
 * Make the service worker's "autoUpdate" actually update the page.
 *
 * vite.config.ts sets `registerType: "autoUpdate"`, but the registration script
 * VitePWA emits is the bare one-liner — it registers /sw.js and nothing else.
 * Meanwhile sw.js is built with skipWaiting + clientsClaim and answers
 * navigations from a precached index.html. The combination means a tab that was
 * open across a deploy keeps running the OLD bundle, and reloading does not
 * help: the reload's own document request is served from that same precache.
 *
 * Proven locally by serving build A, letting the worker install, swapping the
 * server to build B, and reloading — the page still requested build A's entry
 * chunk. That is why shipped fixes appeared not to arrive on a device even
 * after a refresh.
 *
 * The missing half is this: when a new worker takes control, reload once so the
 * page picks up the new index.html and its chunk graph.
 */

/** Timestamp of the last reload this tab performed, as a loop brake. */
const RELOAD_GUARD = 'cmc_sw_reloaded_at';

/**
 * How close together two reloads have to be to count as a loop rather than two
 * genuine deploys. Long enough that a tight reload cycle can never sustain
 * itself, short enough that a second deploy later in the same session still
 * lands. An earlier version of this file used a plain boolean here, which
 * blocked EVERY deploy after the first for the lifetime of the tab — the exact
 * staleness bug this module exists to fix, quietly reintroduced.
 */
const LOOP_WINDOW_MS = 30_000;

/**
 * Is there text on screen that a reload would throw away?
 *
 * The reload can land at any moment, including mid-sentence. Some flows persist
 * as you type (the onboarding wizard, lesson drafts); others hold everything in
 * component state until submit — the cover feedback wizard keeps a teacher's
 * free-text notes on misconceptions and what worked entirely in memory. Wiping
 * those without warning would be worse than the stale bundle this fixes.
 *
 * Rather than teach this module about individual components, it asks the DOM:
 * if any text field currently holds anything, the page is not safe to reload
 * yet. Prefilled fields make this cautious rather than wrong — a cautious
 * answer only delays the reload to a safer moment.
 */
function hasTypedContent(): boolean {
  const TEXTUAL = /^(text|email|search|tel|url|password|number)$/;
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  for (const el of fields) {
    if (el.tagName === 'TEXTAREA' || TEXTUAL.test((el as HTMLInputElement).type)) {
      if (el.value.trim() !== '') return true;
    }
  }
  return !!document.querySelector('[contenteditable="true"]:not(:empty)');
}

export function initServiceWorkerAutoReload() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  // Whether this page load was ALREADY under a worker's control. On a first
  // ever visit it is not: the freshly installed worker calls clientsClaim(),
  // which fires controllerchange for the very first time. Reloading on that
  // would mean every new visitor's first page load reloads itself for nothing,
  // so the first claim is absorbed rather than acted on.
  let hadController = !!navigator.serviceWorker.controller;

  const reload = () => {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD) ?? 0);
      if (Date.now() - last < LOOP_WINDOW_MS) return;
      sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
    } catch {
      // Storage unavailable (private browsing): skip the reload rather than
      // run unguarded, where a re-claiming worker could loop.
      return;
    }
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }

    if (!hasTypedContent()) {
      reload();
      return;
    }

    // Something is half-typed. Wait for a moment when nobody is looking — the
    // tab being hidden — and pick the new build up then. If that never happens
    // the tab simply stays on the current build, which is no worse than the
    // behaviour before this module existed.
    const onHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      document.removeEventListener('visibilitychange', onHidden);
      reload();
    };
    document.addEventListener('visibilitychange', onHidden);
  });
}
